import { useEffect, useCallback } from 'react';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { usePageLayoutResize } from '@atlassian/jira-navigation-system';
import { useResource } from '@atlassian/jira-router';
import {
	navigationUiStateResource,
	NAVIGATION_UI_STORAGE_KEY,
} from '@atlassian/jira-router-resources-navigation-ui-state';
import { toBaseUrl } from '@atlassian/jira-shared-types';
import { useAccountId } from '@atlassian/jira-tenant-context-controller';
import { useLayoutStoreActions, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store';
import { useRightSidebarState } from '../right-sidebar-controller';
/**
 * Synchronizes the layout's local state with the remote server to ensure consistency across sessions.
 * It monitors changes in the layout, such as sidebar adjustments, and updates the server accordingly.
 * Critical errors encountered during the update process are captured and reported to facilitate
 * troubleshooting and continuous improvement. This component plays a pivotal role in maintaining
 * a seamless user experience by ensuring that layout preferences are consistently reflected.
 */
export const LayoutRemoteStateUpdater = () => {
	// left-sidebar
	const { leftSidebarState } = usePageLayoutResize();
	const { isFlyoutOpen, isLeftSidebarCollapsed, leftSidebarWidth } = leftSidebarState;
	const resolvedLeftSidebarWidth: number = Math.max(leftSidebarWidth, DEFAULT_LEFT_SIDEBAR_WIDTH);
	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const isCollapsed: boolean = leftSidebarState.isLeftSidebarCollapsed;

	// right-sidebar
	const rightSidebarState = useRightSidebarState();
	const accountId = useAccountId();
	const baseUrl = toBaseUrl('');
	const { data, loading, error } = useResource(navigationUiStateResource);
	const isLayoutStateReady = leftSidebarWidth !== 0 && leftSidebarState.lastLeftSidebarWidth !== 0;
	useEffect(() => {
		if (!isLayoutStateReady) return;
		if (error) return;
		setLeftSidebarState({
			width: resolvedLeftSidebarWidth,
			isCollapsed,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			isFlyoutOpen: isFlyoutOpen as boolean,
			isVisible: true,
		});
	}, [
		error,
		isCollapsed,
		isFlyoutOpen,
		isLayoutStateReady,
		resolvedLeftSidebarWidth,
		setLeftSidebarState,
	]);
	const updateData = useCallback(async () => {
		if (loading) return;
		if (error) return;
		if (!isLayoutStateReady) return;
		const remoteData = {
			...data,
			leftSidebar: {
				width: resolvedLeftSidebarWidth,
				isCollapsed: isLeftSidebarCollapsed,
			},
			rightSidebar: rightSidebarState,
		};
		if (accountId) {
			try {
				await setUserProperties(
					baseUrl,
					accountId,
					NAVIGATION_UI_STORAGE_KEY,
					JSON.stringify(remoteData),
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'updateServerLayoutState',
						packageName: 'jiraLayoutController',
						teamName: 'magma',
					},
				});
			}
		}
	}, [
		accountId,
		baseUrl,
		data,
		error,
		isLayoutStateReady,
		isLeftSidebarCollapsed,
		loading,
		resolvedLeftSidebarWidth,
		rightSidebarState,
	]);
	useEffect(() => {
		updateData();
	}, [isLeftSidebarCollapsed, resolvedLeftSidebarWidth, updateData]);
	return null;
};
